import React from "react";
import { useParams } from "react-router-dom";
import { Header } from "../components/shared";
import { CheckoutFooter } from "../components/checkout";
import {
  SaleProductInfo,
  SaleContactForm,
  FeaturesList,
  TechnicalDetails,
  FAQSection,
} from "../components/saleDetails";
import { MEDIA_URL } from "../config/constants";
import "../styles/saleDetails/SaleDetails.css";

const SaleDetails = () => {
  const { id } = useParams();

  // Mock data - in real app this would come from API
  const campervanData = {
    1: {
      title: "Transporter T6.1 / T7 California",
      subtitle: "Premium Nomad Van for Adventure Seekers",
      price: "$35,000",
      status: "Pre-order",
      mainImage:
        `${MEDIA_URL}/images/placeholders/campervan-detail-1.jpg`,
      images: [
        `${MEDIA_URL}/images/placeholders/campervan-detail-1.jpg`,
        `${MEDIA_URL}/images/placeholders/campervan-detail-2.jpg`,
        `${MEDIA_URL}/images/placeholders/campervan-detail-3.jpg`,
        `${MEDIA_URL}/images/placeholders/campervan-detail-4.jpg`,
      ],
      description:
        "Experience freedom on the road with this premium Transporter T6.1 California. Perfect for nomads and adventure seekers, this van combines comfort, reliability, and style in one exceptional package.",
      features: {
        bathroom: [
          { icon: "shower", description: "Hot Water Shower" },
          { icon: "toilet", description: "Cassette Toilet" },
        ],
        bedroom: [
          { icon: "bedding", description: "Queen Size Bed" },
          { icon: "bunk-bed", description: "Convertible Seating" },
        ],
        outdoor: [
          { icon: "awning", description: "Retractable Awning" },
          { icon: "storage", description: "External Storage" },
        ],
        kitchen: [
          { icon: "stove", description: "2-Burner Stove" },
          { icon: "fridge", description: "Compressor Fridge" },
          { icon: "sink", description: "Fresh Water Sink" },
        ],
      },
      specifications: {
        year: "2023",
        mileage: "85,000 KM",
        transmission: "6-Speed Automatic",
        engine: "2.5L Turbo Diesel",
        fuel: "Diesel",
        sleeps: "2-4 People",
        length: "5.99m",
        width: "2.30m",
        height: "2.84m",
      },
      technicalDetails: [
        {
          icon: "van",
          title: "Model",
          description: "T6.1 California",
        },
        {
          icon: "engine",
          title: "Engine",
          description: "2.5L Turbo Diesel",
        },
        {
          icon: "dimensions",
          title: "Dimensions",
          description: "5.99m x 2.30m x 2.84m",
        },
        {
          icon: "assistance",
          title: "Transmission",
          description: "6-Speed Automatic",
        },
      ],
      faqs: [
        {
          question: "What's included in the sale price?",
          answer:
            "The sale price includes the fully converted campervan with all equipment, appliances, and furnishings as shown. We also provide a 2-year comprehensive warranty on the conversion.",
          isOpen: true,
        },
        {
          question: "Can I arrange financing?",
          answer:
            "Yes, we work with several financing partners to offer competitive rates. Our sales team can help you explore financing options that fit your budget.",
          isOpen: false,
        },
        {
          question: "Is there a warranty on the conversion?",
          answer:
            "All our conversions come with a comprehensive 2-year warranty covering all conversion work, appliances, and electrical systems. The base vehicle warranty remains as per manufacturer terms.",
          isOpen: false,
        },
        {
          question: "Can I inspect the campervan before purchase?",
          answer:
            "Absolutely! We encourage all potential buyers to inspect the vehicle in person. Please contact us to schedule a viewing appointment.",
          isOpen: false,
        },
        {
          question: "Do you accept trade-ins?",
          answer:
            "Yes, we accept trade-ins on most vehicles. Our team can provide a fair market evaluation of your current vehicle to apply toward your purchase.",
          isOpen: false,
        },
      ],
    },
    2: {
      title: "Volkswagen Transporter T6.1",
      subtitle: "Perfect Family Adventure Van",
      price: "$35,000",
      status: "Available",
      mainImage:
        `${MEDIA_URL}/images/placeholders/campervan-detail-2.jpg`,
      images: [
        `${MEDIA_URL}/images/placeholders/campervan-detail-2.jpg`,
        `${MEDIA_URL}/images/placeholders/campervan-detail-1.jpg`,
        `${MEDIA_URL}/images/placeholders/campervan-detail-3.jpg`,
        `${MEDIA_URL}/images/placeholders/campervan-detail-4.jpg`,
      ],
      description:
        "Ideal for family adventures, this T6.1 Transporter offers spacious comfort and reliable performance. With family-friendly features and proven Volkswagen quality.",
      features: {
        bathroom: [
          { icon: "shower", description: "Compact Shower" },
          { icon: "toilet", description: "Portable Toilet" },
        ],
        bedroom: [
          { icon: "bedding", description: "Family Sleeping Area" },
          { icon: "bunk-bed", description: "Bunk Beds" },
        ],
        outdoor: [
          { icon: "awning", description: "Side Awning" },
          { icon: "storage", description: "Rear Storage Box" },
        ],
        kitchen: [
          { icon: "stove", description: "Compact Cooktop" },
          { icon: "fridge", description: "12V Fridge" },
          { icon: "sink", description: "Water System" },
        ],
      },
      specifications: {
        year: "2022",
        mileage: "40,000 KM",
        transmission: "5-Speed Manual",
        engine: "2.0L Diesel",
        fuel: "Diesel",
        sleeps: "4-6 People",
        length: "5.99m",
        width: "2.30m",
        height: "2.84m",
      },
      technicalDetails: [
        {
          icon: "van",
          title: "Model",
          description: "T6.1 Transporter",
        },
        {
          icon: "engine",
          title: "Engine",
          description: "2.0L Diesel",
        },
        {
          icon: "dimensions",
          title: "Dimensions",
          description: "5.99m x 2.30m x 2.84m",
        },
        {
          icon: "assistance",
          title: "Transmission",
          description: "5-Speed Manual",
        },
      ],
      faqs: [
        {
          question:
            "What's the difference between this and the California model?",
          answer:
            "This T6.1 Transporter offers a more family-focused conversion with additional seating and storage, while the California focuses on premium nomadic features.",
          isOpen: true,
        },
        {
          question: "Is this suitable for family trips?",
          answer:
            "Yes! This model is specifically designed for families with comfortable seating for up to 6 people and ample storage for family gear.",
          isOpen: false,
        },
        {
          question: "What's the fuel economy like?",
          answer:
            "The 2.0L diesel engine typically achieves 25-30 MPG depending on driving conditions and load, making it economical for long road trips.",
          isOpen: false,
        },
        {
          question: "Are there any recent service records?",
          answer:
            "Yes, we maintain complete service records for all our vehicles. This Transporter has been regularly serviced and is in excellent mechanical condition.",
          isOpen: false,
        },
        {
          question: "Can modifications be made to the conversion?",
          answer:
            "We can discuss custom modifications during the sales process. Some changes may be possible depending on your specific needs and requirements.",
          isOpen: false,
        },
      ],
    },
  };

  const campervan = campervanData[id] || campervanData[1];

  return (
    <div className="sale-details-page">
      <Header />
      <main className="sale-details-main">
        <div className="sale-details-container">
          <div className="sale-content-wrapper">
            <div className="sale-left-content">
              <SaleProductInfo campervan={campervan} />
              <FeaturesList features={campervan.features} />
              <TechnicalDetails details={campervan.technicalDetails} />
              <FAQSection faqs={campervan.faqs} />
            </div>
            <div className="sale-right-content">
              <SaleContactForm campervan={campervan} />
            </div>
          </div>
        </div>
      </main>
      <CheckoutFooter />
    </div>
  );
};

export default SaleDetails;
